import * as Api from '@ViewModels';
import moment from 'moment';
import { getDiscountName } from '../../../models/utils/stringUtils';

/**
 * Pure display formatters for the admin Account Overview, ported 1:1 from the computed
 * getters on the MobX `AccountOperationsViewModel` so the overview can drop MobX while
 * preserving exact display behavior (fallbacks, date formats, the Today/Tomorrow rule).
 *
 * Each takes an `IAccount` and returns a display string; all tolerate a missing account.
 */

const capitalizeWord = (value: string): string =>
	`${value.charAt(0).toLocaleUpperCase()}${value.slice(1).toLocaleLowerCase()}`;

export const accountStatusDescription = (account?: Api.IAccount): string =>
	account?.activationStatus ? capitalizeWord(account.activationStatus) : 'Unknown';

export const billingTypeDescription = (account?: Api.IAccount): string =>
	account?.planDetails?.billingType ? capitalizeWord(account.planDetails.billingType) : 'Unknown';

export const isPaidAccount = (account?: Api.IAccount): boolean =>
	account?.planDetails?.billingType === Api.BillingType.Paid ||
	account?.planDetails?.billingType === Api.BillingType.Comp;

export const isCompAccount = (account?: Api.IAccount): boolean =>
	account?.planDetails?.billingType === Api.BillingType.Comp;

/**
 * True once the trial's expiration date is in the past. Legacy compared against `moment(undefined)`,
 * which is *now*, so an account with no expiration date got a nondeterministic answer; absent means
 * not expired here.
 */
export const isTrialExpired = (account?: Api.IAccount): boolean => {
	const expiration = account?.planDetails?.trialExpirationDate;
	if (!expiration) {
		return false;
	}
	return moment().diff(moment(expiration)) >= 0;
};

export const creationDateDescription = (account?: Api.IAccount): string =>
	account?.creationDate ? moment(account.creationDate).format('MM/DD/YYYY') : 'Unknown';

export const renewalOrExpireDateDescription = (account?: Api.IAccount): string => {
	const billingType = account?.planDetails?.billingType;
	if (billingType === Api.BillingType.Paid) {
		return moment(account?.planDetails?.renewalDate).format('MM/DD/YYYY');
	}
	if (billingType === Api.BillingType.Trial) {
		return moment(account?.planDetails?.trialExpirationDate).format('MM/DD/YYYY');
	}
	return 'N/A'; // Comp
};

export const lastCsmMeetingDescription = (account?: Api.IAccount): string =>
	account?.additionalInfo?.lastCsmMeeting
		? moment(account.additionalInfo.lastCsmMeeting).format('MM/DD/YYYY')
		: 'Unknown';

/** Today / Tomorrow / MM/DD/YYYY relative formatting (UTC compare), mirroring the legacy VM. */
export const relativeMeetingDescription = (value?: Date | string | null): string => {
	if (!value) {
		return 'Not Set';
	}
	const momentValue = moment(value).utc();
	if (momentValue.isSame(moment().startOf('day'), 'd')) {
		return 'Today';
	}
	if (momentValue.isSame(moment().add(1, 'days').startOf('day'), 'd')) {
		return 'Tomorrow';
	}
	return momentValue.format('MM/DD/YYYY');
};

export const nextMeetingDescription = (account?: Api.IAccount): string =>
	relativeMeetingDescription(account?.additionalInfo?.nextMeeting);

export const billingCycleDescription = (account?: Api.IAccount): string => {
	const billingType = account?.planDetails?.billingType;
	const billingCycle = account?.planDetails?.billingCycle;
	return billingType === Api.BillingType.Paid && billingCycle ? `${billingCycle} months` : 'N/A';
};

/**
 * True when Chargify reports the subscription as past due. `billingState` is a free-form string
 * off the billing provider, so it is compared case-insensitively.
 */
export const isPastDueAccount = (account?: Api.IAccount): boolean =>
	account?.planDetails?.billingState?.toLowerCase() === 'past_due';

/** A cancellation is on file but the account is still active — distinct from an already-CANCELED one. */
export const isPendingCancellation = (account?: Api.IAccount): boolean =>
	account?.activationStatus !== Api.ActivationStatus.CANCELED && !!account?.planDetails?.pendingCancellation;

const EM_DASH = '—';

/** MRR display: "$<planMRR>" or "$—" when missing. */
export const mrrDisplay = (account?: Api.IAccount): string => `$${account?.planDetails?.planMRR ?? EM_DASH}`;

/** Extra users display: users count minus the 5 included seats, or "—" when unset. */
export const extraUsersDisplay = (account?: Api.IAccount): string => {
	const users = account?.planDetails?.users;
	return users != null ? `${users - 5}` : EM_DASH;
};

/** Last billing amount display: "$<amount>" or "$—". */
export const lastBillingAmountDisplay = (account?: Api.IAccount): string =>
	`$${account?.planDetails?.lastBill?.amount ?? EM_DASH}`;

/** Last billing date formatted MM/DD/YYYY, or "—" when unset. */
export const lastBillingDateDisplay = (account?: Api.IAccount): string => {
	const date = account?.planDetails?.lastBill?.date;
	return date ? moment(date).format('MM/DD/YYYY') : EM_DASH;
};

/**
 * Renewal discount display for the Plan Details hover: the *applied* Chargify discount by name
 * (`planDetails.discountApplied`), which is a different field from the editable
 * `additionalInfo.renewalDiscountPercentage` the Billing card shows as its own value.
 */
export const appliedRenewalDiscountDisplay = (account?: Api.IAccount): string => {
	const discount = account?.planDetails?.discountApplied;
	return discount != null ? `${getDiscountName(discount)} renewal discount` : 'No renewal discount';
};

export const industryDescription = (account?: Api.IAccount): string => account?.additionalInfo?.industry || 'Unknown';

export const soldByDescription = (account?: Api.IAccount): string => account?.additionalInfo?.salesRepName || 'Unknown';

export const successManagerDescription = (account?: Api.IAccount): string =>
	account?.additionalInfo?.customerSuccessName || 'Unknown';

export const stageDescription = (account?: Api.IAccount): string => account?.additionalInfo?.stage || 'None';

export const brokerDealerDescription = (account?: Api.IAccount): string =>
	account?.additionalInfo?.brokerDealer || 'Unknown';

// Optional-chains `additionalInfo` (the legacy getter did not, and threw when it was null).
export const systemOfRecordDescription = (account?: Api.IAccount): string =>
	account?.additionalInfo?.systemOfRecord || 'Unknown';

/**
 * Human-readable list of an account's add-on components, ported 1:1 from the legacy
 * `getAccountComponentsLabel` helper. Starts from `planDetails.additionalComponents`,
 * then folds in two comped feature flags:
 *  - `customContentCalendar.enabled` appends "Custom Content" (or "Custom Content (Comp)"
 *    outside a prestige bundle) unless a Custom Content component is already listed;
 *  - `websites.enabled` unshifts "Website" to the front unless a website component exists.
 * Known names are abbreviated (Website→Web, Facebook, Google, Custom Content); unknown
 * names pass through verbatim. Empty → 'None'.
 */
export const getAccountComponentsLabel = (account?: Api.IAccount): string => {
	const abbreviateComponent = (name: string): string => {
		const lower = name.toLowerCase();
		if (lower.includes('website')) return 'Web';
		if (lower.includes('custom content')) return 'Custom Content';
		if (lower.includes('facebook')) return 'Facebook';
		if (lower.includes('google')) return 'Google';
		return name;
	};

	const components = [...(account?.planDetails?.additionalComponents ?? [])];

	const hasCustomContent = components.some(c => c.toLowerCase().includes('custom content'));
	if (!hasCustomContent && account?.features?.customContentCalendar?.enabled) {
		const planName = account?.planDetails?.planName?.toLowerCase() ?? '';
		const isPrestigeBundle = planName.includes('prestige') && !planName.includes('standalone');
		components.push(isPrestigeBundle ? 'Custom Content' : 'Custom Content (Comp)');
	}

	if (account?.features?.websites?.enabled && !components.some(c => c.toLowerCase().includes('website'))) {
		components.unshift('Website');
	}

	if (components.length === 0) return 'None';

	return components.map(abbreviateComponent).join(', ');
};

/**
 * Label for the renewal/expiration date field. A Trial account has no renewal — legacy titled the
 * same field "Expiration Date" for Trials, so calling it "Renewal Date" there misreads the value.
 */
export const renewalDateLabel = (account?: Api.IAccount): string =>
	account?.planDetails?.billingType === Api.BillingType.Trial ? 'Expiration Date' : 'Renewal Date';

/**
 * Pure port of the legacy `isStillInContract`: true when the account is on a sub-annual
 * paid plan (positive Chargify `billingReferenceId`, `billingCycle` < 12) whose renewal
 * falls inside the first year after creation. Any missing prerequisite → false.
 */
export const isStillInContract = (account?: Api.IAccount): boolean => {
	const planDetails = account?.planDetails;
	if (!planDetails?.billingReferenceId || planDetails.billingReferenceId <= 0) {
		return false;
	}
	if (!planDetails.billingCycle || planDetails.billingCycle >= 12) {
		return false;
	}
	const creationDate = account?.creationDate;
	const renewalDate = planDetails?.renewalDate;
	if (!creationDate || !renewalDate) {
		return false;
	}
	const oneYearAfterCreation = moment(creationDate).add(1, 'year');
	return moment(renewalDate).isBefore(oneYearAfterCreation);
};

/**
 * Display for the "will renew" prediction, ported from `renderWillRenewPredictionValue`:
 * null/undefined → 'Not Selected'; an all-digits string → 'Downgrade (n%)'; anything else
 * is stringified verbatim (a numeric value is NOT treated as a downgrade percentage).
 */
export const willRenewPredictionDisplay = (value?: string | number): string => {
	if (value === undefined || value === null) {
		return 'Not Selected';
	}

	if (typeof value === 'string' && /^\d+$/.test(value)) {
		return `Downgrade (${value}%)`;
	}

	return value.toString();
};

/**
 * Comma-joined list of an account's enabled integrations, ported from
 * `getEnabledIntegrationsSummary`. Known keys map to friendly names; unknown enabled keys
 * pass through verbatim. Appends 'Zapier' when `features.publicApi.enabled`. Empty → 'None'.
 */
export const getEnabledIntegrationsSummary = (account?: Api.IAccount): string => {
	const integrations = account?.integrations;
	if (!integrations) {
		return 'None';
	}

	const integrationNames: Record<string, string> = {
		ams360: 'AMS360',
		clio: 'Clio',
		eclipse: 'Eclipse',
		epicCsv: 'Epic',
		ezLynxCsv: 'EZLynx',
		hawkSoft: 'HawkSoft',
		qqCatalyst: 'QQ Catalyst',
		redtail: 'Redtail',
		hubSpot: 'HubSpot',
		generalCsv: 'General CSV',
		donorPerfect: 'DonorPerfect',
	};

	const enabled = Object.entries(integrations)
		.filter(([, value]) => (value as { enabled?: boolean } | undefined)?.enabled)
		.map(([key]) => integrationNames[key] || key);

	if (account?.features?.publicApi?.enabled) {
		enabled.push('Zapier');
	}

	return enabled.length > 0 ? enabled.join(', ') : 'None';
};
